import { Component, inject } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { AppNotificationService } from '@app/core/services/custom-services/notification.service';
import { _, TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { NgxSpinnerService, NgxSpinnerModule } from 'ngx-spinner';
import { EntityService } from '@app/core/services/http-services/model/entity.service';
import { DialogRef, DialogContentBase, KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { EntityStatusEnum } from '@app/core/models/entity-status.enum';
import { IBulkEditEntityForm, IBulkEditStateForm, IBulkEditPropertyForm, IBulkEditUnitConversionForm } from '@app/core/models/forms/entity-admin/entity-form/entity-bulk-edit-state-form.model';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABELS } from '@progress/kendo-angular-label';
import { SelectEntityStatusComponent } from '../../../app-controls/select-entity-status/select-entity-status.component';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { CommonModule } from '@angular/common';
import { SelectUnitComponent } from '@app/features/app-controls/select-unit/select-unit.component';
import { SelectPropertyComponent } from '@app/features/app-controls/select-property/select-property.component';
import { SelectValidValueComponent } from '@app/features/app-controls/select-valid-value/select-valid-value.component';
import { IEntityProperty } from '@app/core/models/entity-property.model';
import { EntityPropertyService } from '@app/core/services/http-services/model/entity-property.service';
import { SVGIcon, cancelIcon, saveIcon } from '@progress/kendo-svg-icons';

_('EntityTranslation.EntityUpdated');

export function conditionalRequired(valueFields: string[]): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const changeControl = formGroup.get('change');

    if (changeControl?.value !== true) {
      return null;
    }

    for (const valueField of valueFields) {
      const valueControl = formGroup.get(valueField);

      if (valueControl.value == null || valueControl.value === '') {
        return { required: true };
      }
    }

    return null;
  };
}

@Component({
  selector: 'app-entity-bulk-edit',
  templateUrl: './entity-bulk-edit.component.html',
  styleUrls: ['./entity-bulk-edit.component.scss'],
  standalone: true,
  imports: [
    TranslateDirective,
    NgxSpinnerModule,
    ReactiveFormsModule,
    SelectEntityStatusComponent,
    SelectUnitComponent,
    SelectPropertyComponent,
    SelectValidValueComponent,
    TranslatePipe,
    CommonModule,
    KENDO_DIALOG,
    KENDO_BUTTONS,
    KENDO_INPUTS,
    KENDO_LABELS,
  ],
})
export class EntityBulkEditComponent extends DialogContentBase {
  EntityStatesData: EntityStatusEnum[] = [];

  anyChange: ValidatorFn = (formGroup: AbstractControl): ValidationErrors | null => {
    const stateChange = formGroup.get('state').get('change');
    const propertyChange = formGroup.get('property').get('change');
    const unitConversionChange = formGroup.get('unitConversion').get('change');

    if (stateChange.value === false && propertyChange.value === false && unitConversionChange.value === false) {
      return { required: true };
    }

    return null;
  };

  updateEntityForm: FormGroup<IBulkEditEntityForm> = {} as FormGroup<IBulkEditEntityForm>;

  public props: IEntityProperty[] = [];

  private readonly entityService = inject(EntityService);
  private readonly entityPropertyService = inject(EntityPropertyService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly dialogRef = inject(DialogRef);
  private readonly appNotificationService = inject(AppNotificationService);
  private readonly formBuilder = inject(FormBuilder);

  public saveIcon: SVGIcon = saveIcon;
  public cancelIcon: SVGIcon = cancelIcon;

  constructor(dialogRef: DialogRef) {
    super(dialogRef);
  }

  public setupFrom(entityIndexes: number[], entityGroupIndex: number) {
    this.entityPropertyService.getEntityPropertysForGroup(entityGroupIndex).subscribe(s => {
      this.props = s;
    });

    this.updateEntityForm = this.formBuilder.group<IBulkEditEntityForm>(
      {
        indexes: this.formBuilder.control<number[]>(entityIndexes),
        state: this.formBuilder.group<IBulkEditStateForm>(
          {
            change: this.formBuilder.control<boolean>(false),
            status: this.formBuilder.control<number | null>(null),
          },
          { validators: conditionalRequired(['status']) }
        ),
        property: this.formBuilder.group<IBulkEditPropertyForm>(
          {
            change: this.formBuilder.control<boolean>(false),
            code: this.formBuilder.control<string | null>(null),
            value: this.formBuilder.control<string | null>(null),
          },
          { validators: conditionalRequired(['code', 'value']) }
        ),
        unitConversion: this.formBuilder.group<IBulkEditUnitConversionForm>(
          {
            change: this.formBuilder.control<boolean>(false),
            unitCode: this.formBuilder.control<string | null>(null),
            convFactor: this.formBuilder.control<number | null>(null),
          },
          { validators: conditionalRequired(['unitCode', 'convFactor']) }
        ),
      },
      { validators: this.anyChange }
    );

    this.updateEntityForm
      .get('state')
      .get('status')
      .valueChanges.subscribe(state => {
        if (state) {
          this.updateEntityForm.get('state').get('change').patchValue(true, { emitEvent: false });
        }
      });

    this.updateEntityForm
      .get('property')
      .get('code')
      .valueChanges.subscribe(state => {
        if (state) {
          this.updateEntityForm.get('property').get('change').patchValue(true, { emitEvent: false });
        }
      });

    this.updateEntityForm
      .get('unitConversion')
      .get('unitCode')
      .valueChanges.subscribe(state => {
        if (state) {
          this.updateEntityForm.get('unitConversion').get('change').patchValue(true, { emitEvent: false });
        }
      });
  }

  async save() {
    this.spinner.show();

    this.entityService.bulkUpdate(this.updateEntityForm.value).subscribe({
      next: responses => {
        this.spinner.hide();
        this.appNotificationService.notifySucsessAppChanel('EntityTranslation.EntityUpdated');
        this.dialogRef.close(responses);
      },
      error: err => {
        this.spinner.hide();
        this.appNotificationService.notifyErrorAppChanel(err);
      },
    });
  }

  close(message: boolean) {
    this.dialogRef.close(message);
  }
  getPropertyByCode(code: string): IEntityProperty {
    return this.props.find(p => p.code === code) ?? null;
  }
}
